import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@mui/material';
import { isEqual } from 'lodash';
import { ReleaseVersionSelect } from 'components/common/firmware/ReleaseVersionSelect';
import type { ReleaseVersion } from 'api/admin/firmware/firmware';
import { DeviceType } from 'api/admin/washboard/washboard';
import { useSetStoreDefaultReleases } from 'api/queries/useSetStoreDefaultReleases';
import type { StoreDefaultRelease } from 'types/store';
import styles from './StoreDefaultReleases.module.scss';

const DEFAULT_RELEASE_DEVICE_TYPES = [DeviceType.DIME, DeviceType.PENNY, DeviceType.PULSE] as const;

type DefaultReleaseDeviceType = (typeof DEFAULT_RELEASE_DEVICE_TYPES)[number];

type SelectedReleases = Record<DefaultReleaseDeviceType, ReleaseVersion | null>;

const DEVICE_TYPE_LABELS: Record<DefaultReleaseDeviceType, string> = {
  [DeviceType.DIME]: 'Dime',
  [DeviceType.PENNY]: 'Penny',
  [DeviceType.PULSE]: 'Pulse',
};

const toSelectedReleases = (defaultReleases: StoreDefaultRelease[]): SelectedReleases =>
  DEFAULT_RELEASE_DEVICE_TYPES.reduce((selected, deviceType) => {
    const defaultRelease = defaultReleases.find((item) => item.deviceType === deviceType);

    return {
      ...selected,
      [deviceType]: defaultRelease
        ? { id: defaultRelease.releaseId, version: defaultRelease.version }
        : null,
    };
  }, {} as SelectedReleases);

interface StoreDefaultReleasesProps {
  defaultReleases?: StoreDefaultRelease[];
  storeId: number;
  onUpdateSuccess?: () => void;
}

export const StoreDefaultReleases: React.FC<StoreDefaultReleasesProps> = ({
  defaultReleases,
  storeId,
  onUpdateSuccess,
}) => {
  const savedReleases = useMemo(() => toSelectedReleases(defaultReleases ?? []), [defaultReleases]);
  const [selectedReleases, setSelectedReleases] = useState<SelectedReleases>(savedReleases);

  const { mutate: saveDefaultReleases, isLoading: isSaving } = useSetStoreDefaultReleases({
    successCallback: onUpdateSuccess,
  });

  useEffect(() => {
    setSelectedReleases(savedReleases);
  }, [savedReleases]);

  const hasChanges = useMemo(
    () => !isEqual(selectedReleases, savedReleases),
    [selectedReleases, savedReleases],
  );

  const handleChange = (deviceType: DefaultReleaseDeviceType, release: ReleaseVersion | null) => {
    setSelectedReleases((current) => ({ ...current, [deviceType]: release }));
  };

  const handleCancel = () => {
    setSelectedReleases(savedReleases);
  };

  const handleSave = () => {
    const payload = DEFAULT_RELEASE_DEVICE_TYPES.flatMap((deviceType) => {
      const release = selectedReleases[deviceType];

      return release ? [{ deviceType, releaseId: release.id }] : [];
    });

    saveDefaultReleases({ storeId, defaultReleases: payload });
  };

  return (
    <div className={styles.defaultReleases}>
      <p className={styles.description}>
        The firmware release assigned to newly paired devices at this location, per device type.
      </p>

      <div className={styles.fields}>
        {DEFAULT_RELEASE_DEVICE_TYPES.map((deviceType) => (
          <div key={deviceType} className={styles.field}>
            <ReleaseVersionSelect
              id={`default-release-${deviceType.toLowerCase()}`}
              deviceType={deviceType}
              selectedRelease={selectedReleases[deviceType]}
              onChange={(release) => handleChange(deviceType, release)}
              label={`${DEVICE_TYPE_LABELS[deviceType]} Release`}
              size="small"
              disabled={isSaving}
            />
          </div>
        ))}
      </div>

      {hasChanges && (
        <div className="configuration-actions">
          <Button variant="outlined" color="primary" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={isSaving}>
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
};
